/* eslint-disable array-callback-return */

import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CellProps, ColumnWithLooseAccessor, Row } from "react-table";
import {
  BillingDetail,
  BillPayStep,
  CtBillDetailType,
  CtTaxInvoiceType,
  ScheduleRoundNOverdueItem,
  TaxInvoiceProgressStatusList,
  TaxPurposeType,
} from "src/api/billingSchedule/billing-type";
import { editTaxInvoiceNumber, mntBillUpdate, retryPayment } from "src/api/billingSchedule/billingSchedule-api";
import { DetailTotalData } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { Modal } from "src/api/public-types";
import { BaseButton, BaseInput, BaseModal, BaseTooltip } from "src/components";
import { objDiff } from "src/pages/contract/hooks/useContractApi";
import { useToast } from "src/recoil/toast/hook";
import { numberToStringWithComma, ViewYmdFormat, YmdFormat } from "src/utils/common-util";
import MakeBillPopup from "../components/MakeBillPopup";
import PaymentStatusView from "../components/PaymentStatusView";
import { BillOrganizationMap } from "../hooks/useBillOrganizations";
import StorageProcessingModal from "../modal/StorageProcessing/StorageProcessingModal";

export type ScheduleColumn = Omit<ColumnWithLooseAccessor<ExtendedScheduleRoundNOverdueItem>, "Cell" | "accessor"> & {
  sticky?: "left";
  accessor?: string;
  Cell?: ((props: ScheduleCellProps) => React.ReactElement | null) | React.FC<ScheduleCellProps>;
};

// original / changed 비교 위해 타입 정의
type BillDetailItem = {
  adjustAmount: number;
  // baseAmount: number;
  billId: number;
  createdBy: string;
  createdDate: string;
  description: string;
  dtlId: number;
  dtlOrder: number;
  isDeleted: boolean;
  itemName: string;
  modifiedBy: string;
  supplyAmount: number;
  taxAmount: number;
  totalAmount: number;
  trDateTime: string;
};

export type ExtendedBill = Omit<ScheduleRoundNOverdueItem["bill"], "billPayStatus"> & { billPayStatus: BillPayStep };

export type ExtendedScheduleRoundNOverdueItem = Omit<ScheduleRoundNOverdueItem, "bill"> & {
  bill: ExtendedBill;
};

export interface ScheduleRoundNOverdueAssociatedData {
  roundList: ScheduleRoundNOverdueItem[];
  contractBaseInfo: DetailTotalData;
  callList: () => void;
  stopObj: (billing: BillingDetail) => void;
  scheduleId: number;
  showBtn: boolean;
  messageModal: Modal;
  setMessageModal: React.Dispatch<React.SetStateAction<Modal>>;
  sendingBillEmail: (data: { billId: string; billIssueDate: string; sendHistory: string }) => Promise<void>; // 청구서 이메일 발송
  sendingTaxInvoiceEmail: (data: { billId: string; mgtKeys: string[]; invoiceType: InvoiceType }) => void; // 세금계산서 이메일
  handleEditTaxPurposeType?: (data: { scheduleId: string; taxPurposeType: TaxPurposeType; billId: string }) => Promise<void>; // 청구/영수 수정
  openNtsModal: (bill: ExtendedBill, invoiceType: InvoiceType) => Promise<void>; // 국세청 전자세금계산서 발행 모달 오픈
  openTaxInvoiceViewUrl: (mgtKey: string) => Promise<void>;
  billOrganizationMap: BillOrganizationMap; // 청구서별 계약자(조직) 정보. key 는 bill.organizationId
}

type InvoiceType = "TAX_PLUS" | "TAX_NONE";

interface InvoiceCellRendererHelperParams {
  invoiceType: InvoiceType;
  row: Row<ExtendedScheduleRoundNOverdueItem>;
}

type InvoiceCellRenderer = (invoiceType: InvoiceType) => React.FC<ScheduleCellProps>;

type ScheduleCellProps = CellProps<ExtendedScheduleRoundNOverdueItem> & ScheduleRoundNOverdueAssociatedData;

const InvoiceIssueColumnTooltip = () => (
  <BaseTooltip
    touchIcon="QUESTION"
    children={
      <div>
        <p>계산서를 발행하려면, 아래 조건을 확인해주세요.</p>
        <ul className="base-list mt3">
          <li className="font13 text-primary mb3">{"신청/계약 > 계약 및 청구 주체 정보 > ‘사업자' 가 등록되어 있는지 확인하세요."}</li>
          <li className="font13 text-primary mb3">{"신청/계약 > 계약 및 청구 주체 정보 > 국가가 ‘국내’인지 확인하세요."}</li>
          <li className="font13 text-primary mb3">{"신청/계약 > 세금계산서 설정 > 발행 여부 > ‘사용' 인지 확인하세요."}</li>
          <li className="font13 text-primary mb3">{"해당 청구서의 결제수단이 ‘계좌이체' 또는 ‘가상계좌'인지 확인하세요."}</li>
          <li className="font13 text-primary mb3">{"해당 청구서의 계산서가 이미 발행되었는지 확인하세요."}</li>
          <li className="font13 text-primary">{"해당 계약의 프로바이더 > 결제 연동정보 > ‘연동 성공’ 인지 확인하세요."}</li>
        </ul>
      </div>
    }
  />
);

const getTypeMatchedBillItem = ({ invoiceType, row }: InvoiceCellRendererHelperParams) => {
  return invoiceType === "TAX_PLUS"
    ? row.original.bill.billDetailList.find((detail: CtBillDetailType) => detail.taxAmount > 0)
    : row.original.bill.billDetailList.find((detail: CtBillDetailType) => detail.taxAmount === 0);
};

const hasTypeMatchedBillItem = ({ invoiceType, row }: InvoiceCellRendererHelperParams) => {
  return typeof getTypeMatchedBillItem({ invoiceType, row }) !== "undefined";
};

const getIssuedTypeMatchedInvoice = ({ invoiceType, row }: InvoiceCellRendererHelperParams) => {
  return row.original.taxInvoiceList.find((item) => item.invoiceType === invoiceType);
};

const hasIssuedTypeMatchedInvoice = ({ invoiceType, row }: InvoiceCellRendererHelperParams) => {
  return typeof getIssuedTypeMatchedInvoice({ invoiceType, row }) !== "undefined";
};

const getInvoiceIssueCellRednerer: InvoiceCellRenderer = (invoiceType) => {
  return ({ row, contractBaseInfo, openNtsModal, billOrganizationMap }: ScheduleCellProps) => {
    const hasBillItem = hasTypeMatchedBillItem({ invoiceType, row });
    const hasIssuedInvoice = hasIssuedTypeMatchedInvoice({ invoiceType, row });

    // 발행 대상 판정은 계약의 계약자가 아니라 해당 청구서에 저장된 계약자로 한다.
    // 계약자 없이 생성된 과거 청구서는 계약에 계약자를 뒤늦게 등록해도 계약자가 없는 상태로 남아 발행 시 eCT327 이 떨어진다
    const billOrganization = billOrganizationMap?.get(Number(row.original.bill.organizationId ?? 0));

    // 청구서의 계약자가 '사업자' 인지 (계약자 없음 / 개인 이면 발행 불가)
    const isBusinessContractor = Boolean(billOrganization) && !billOrganization?.classifications?.includes("INDIVIDUAL");

    const isBusinessLocationDomestic = billOrganization?.bizLocationType === "DOMESTIC";

    const isUsedTaxInvoice = contractBaseInfo?.scheduleList?.find(
      (schedule: any) => schedule.supplyType === "RENTAL" && schedule.isUseTaxInvoice === true,
    );

    return (
      <div>
        {/* 발행이 완료되었으면 발행완료 버튼 표시 */}
        <BaseButton
          title={"발행"}
          className="color-white size-small"
          disabled={
            !isBusinessContractor || // 청구서의 계약자가 없거나 사업자가 아니면 비활성화
            row.original.bill.pgcode === "creditcard" || // 결제수단이 신용카드이면 비활성화
            row.original.bill.billPayStatus === "TEMP_SAVE" || //
            contractBaseInfo?.provider?.provider?.isVerifiedTaxInvoice === false || // 팝빌 인증정보 검증 실패 시 비활성화
            isUsedTaxInvoice === undefined || // 세금계산서 사용 여부 체크
            !hasBillItem ||
            hasIssuedInvoice ||
            !isBusinessLocationDomestic
          }
          onClick={() => {
            openNtsModal(row.original.bill, invoiceType);
          }}
        />
      </div>
    );
  };
};

const getInvoiceMailCellRenderer: InvoiceCellRenderer = (invoiceType) => {
  return ({ row, sendingTaxInvoiceEmail }) => {
    const hasBillItem = hasTypeMatchedBillItem({ invoiceType, row });
    const billId = row.original.bill.billId;
    const taxInvoiceMgtKeys = row.original.taxInvoiceList.map((item) => item.mgtKey);
    // 청구서에 과세 항목이 있는지 확인

    return (
      <BaseButton
        title="재발송"
        className="color-white size-small"
        disabled={taxInvoiceMgtKeys.length === 0 || !hasBillItem}
        onClick={() => {
          sendingTaxInvoiceEmail({ billId, mgtKeys: taxInvoiceMgtKeys, invoiceType });
        }}
      />
    );
  };
};

const getInvoiceStatusCellRenderer: InvoiceCellRenderer = (invoiceType) => {
  return ({ row, openTaxInvoiceViewUrl }) => {
    // 청구서 > 과세 항목이 있는지
    const matchedTypeItem = getTypeMatchedBillItem({ invoiceType, row });

    // 발행된 세금계산서가 있는지
    const matchedTypeIssuedInvoice = getIssuedTypeMatchedInvoice({ invoiceType, row });

    // 청구서 > 과세 항목이 없으면 하이픈 노출
    if (!matchedTypeItem) {
      return <>-</>;
    }
    // 청구서 > 과세 항목이 있고, 발행된 세금계산서가 없으면 발행전 노출
    else if (matchedTypeItem && !matchedTypeIssuedInvoice) {
      return <div className={`status E`}>발행전</div>;
    }
    // 계산서 발행건이 있으면 표시
    else if (matchedTypeIssuedInvoice) {
      const progressStatus = TaxInvoiceProgressStatusList.find((status) => status.value === matchedTypeIssuedInvoice.progressStatus);
      return (
        <>
          <div className={`status ${progressStatus?.color}`}>{progressStatus?.label}</div>
          <BaseButton
            target="_blank"
            title={"확인"}
            className="color-white size-small ml5"
            onClick={() => {
              const mgtKey = matchedTypeIssuedInvoice.mgtKey;
              openTaxInvoiceViewUrl(mgtKey);
            }}
          />
        </>
      );
    }
    return <></>;
  };
};

const getInvoiceNumberCellRenderer: InvoiceCellRenderer = (invoiceType) => {
  return ({ row, scheduleId, callList, showBtn }) => {
    const [view, setView] = useState(false);
    const [taxInvoice, setTaxInvoice] = useState<Partial<CtTaxInvoiceType>>({ invoiceNumber: "" });
    const [isError, setIsError] = useState(false);
    const [error, setError] = useState("");

    const [originalTaxInvoice, setOriginalTaxInvoice] = useState<CtTaxInvoiceType>();

    const { executeAsync: changeTaxInvoice } = useApiOperation(editTaxInvoiceNumber);

    useEffect(() => {
      if (row.original.taxInvoiceList !== null && Array.isArray(row.original.taxInvoiceList) && row.original.taxInvoiceList.length > 0) {
        const TAX_LIST = row.original.taxInvoiceList.filter((invoice) => invoice.invoiceType === invoiceType);

        const sorted = _.sortBy(TAX_LIST, ["createdDate"], ["DESC"]);
        setTaxInvoice(sorted[0]);
        setOriginalTaxInvoice(sorted[0]);
      }
    }, [row.original.taxInvoiceList]);

    const saveTaxInvoice = useCallback(async () => {
      const pattern = /[a-zA-Z0-9]{24}/;
      if (typeof taxInvoice.invoiceNumber === "undefined" || taxInvoice.invoiceNumber === "") {
        setError("국세청 승인번호는 필수 입력 정보입니다.");
        setIsError(true);
        return;
      } else if (pattern.test(taxInvoice.invoiceNumber) === false) {
        setError("올바르지 않은 국세청 승인번호 형식입니다.");
        setIsError(true);
        return;
      }

      const send = _.cloneDeep(taxInvoice);
      send.billId = row.original.bill.billId;
      send.invoiceType = invoiceType;

      await changeTaxInvoice({ scheduleId, data: send });

      setView(false);
      setError("국세청 승인번호가 변경되었습니다.");
      setIsError(true);
      await callList();
    }, [callList, changeTaxInvoice, row.original.bill.billId, scheduleId, taxInvoice]);

    return (
      <div className="d-flex">
        <div className="flex-center">
          <div className="w-100 text-left mr10">
            {taxInvoice && taxInvoice.invoiceNumber !== undefined && taxInvoice.invoiceNumber !== "" ? (
              <div className="flex-center">
                {showBtn && (
                  <BaseButton
                    title="수정"
                    disabled={Number(row.original.bill.totalAmount) === 0}
                    className="color-white size-small mr10"
                    onClick={() => setView(true)}
                  />
                )}
                <BaseTooltip contents={taxInvoice.invoiceNumber} />
              </div>
            ) : (
              "-"
            )}
          </div>
        </div>

        <BaseModal
          className="dialog-modal small-size"
          isOpen={view}
          btnLeftTitle="취소"
          btnRightTitle="확인"
          onClose={() => {
            setView(false);

            if (originalTaxInvoice) setTaxInvoice(originalTaxInvoice);
          }}
          onClick={() => saveTaxInvoice()}
        >
          <>
            <div className="modal-title border-bottom">
              <div className="left-area align-items-center">
                <span>세금계산서 발행 정보 수정</span>
              </div>
              <div className="right-area"></div>
            </div>
            <div className="contents-scroll">
              <table className="inner-table" width="100%">
                <tbody>
                  <tr>
                    <td colSpan={2} className="text-left font12 text-gray500">
                      <p>* 팝빌에서 자동 발행 된 ‘국세청승인번호’ 가 출력됩니다.</p>
                      <p>* 홈택스를 통해 발행/재발행 시 ‘국세청승인번호(24자리)’ 를 등록 해주세요.</p>
                    </td>
                  </tr>

                  <tr>
                    <td width="180">
                      <div className="flex-center ">
                        <span className="text-gray600">국세청 승인번호</span>
                      </div>
                    </td>
                    <td>
                      <div className="py10">
                        <div className="minmax300 mb8">
                          <BaseInput
                            name={"taxInvoiceNumber"}
                            value={String(taxInvoice?.invoiceNumber)}
                            onChange={(value: string) => {
                              const temp = _.cloneDeep(taxInvoice);
                              temp.invoiceNumber = value;
                              setTaxInvoice(temp);
                            }}
                            maxLength={24}
                            // disabled={true}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        </BaseModal>
        <BaseModal isOpen={isError} btnRightTitle="확인" onClick={() => setIsError(false)}>
          <p>{error}</p>
        </BaseModal>
      </div>
    );
  };
};

const getInvoiceIssueDateCellRenderer: InvoiceCellRenderer = (invoiceType) => {
  return ({ row }) => {
    const [original, setOriginal] = useState<CtTaxInvoiceType>();
    useEffect(() => {
      if (row.original.taxInvoiceList !== null && Array.isArray(row.original.taxInvoiceList) && row.original.taxInvoiceList.length > 0) {
        const TAX_LIST = row.original.taxInvoiceList.filter((invoice) => invoice.invoiceType === invoiceType);

        const sorted = _.sortBy(TAX_LIST, ["createdDate"], ["DESC"]);

        setOriginal(sorted[0]);
      }
    }, [row.original.taxInvoiceList]);
    return <p>{original && original?.createdDate !== "" ? moment(original?.createdDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS) : "-"}</p>;
  };
};

const ScheduleInfoColumns: ScheduleColumn[] = [
  {
    Header: "청구서 명",
    accessor: "bill.billTitle",
    sticky: "left",
    width: 160,
    Cell: (cellProps: ScheduleCellProps) => {
      const { value, row, contractBaseInfo, showBtn, callList: getRounds } = cellProps;

      const { openToast } = useToast();
      //청구서 수정
      const { executeAsync: updateBill } = useApiOperation(mntBillUpdate);
      const [showUpdateBill, setShowUpdateBill] = useState(false);

      const oriBill = row.original.bill;
      const [bill, setBill] = useState(_.cloneDeep(oriBill));

      const [messageModal, setMessageModal] = useState<Modal>({
        isOpen: false,
      });
      const onClosePopup = useCallback(async () => {
        setShowUpdateBill(false);
        await getRounds();
      }, [getRounds]);

      // v.1.32 harry 수정 checkBillChanges 함수로 대응 > 이슈없으면 추후 validateChange 제거예정

      // 개선된 변경사항 체크 함수 (cloneDeep 제거, 성능 개선)
      const checkBillChanges = (detailList?: any): boolean => {
        if (!detailList || !oriBill) return false;

        // 비교에서 제외할 필드 목록
        const excludedKeys = [
          "billDetailList",
          "baseAmount",
          "totalAmount",
          "supplyAmount",
          "taxAmount",
          "modifiedDate",
          "modifiedBy",
          "createdDate",
          "createdBy",
        ];

        // bill 객체에서 제외 필드를 제거한 정규화된 객체 생성
        const normalizeBill = (billObj: any) => {
          const normalized: any = {};
          Object.keys(billObj).forEach((key) => {
            if (!excludedKeys.includes(key)) {
              normalized[key] = billObj[key];
            }
          });
          return normalized;
        };

        // 일반 필드 변경 체크 (정규화된 객체로 비교)
        const normalizedOriBill = normalizeBill(oriBill);
        const normalizedBill = normalizeBill(bill);

        const diffKeys = objDiff(normalizedOriBill, normalizedBill);

        if (diffKeys.length > 0) {
          return true;
        }

        const normalizeToBillDetailItem = (item: any): BillDetailItem => ({
          adjustAmount: item.adjustAmount,
          // baseAmount: item.baseAmount,
          billId: item.billId,
          createdBy: item.createdBy,
          createdDate: item.createdDate,
          description: item.description,
          dtlId: item.dtlId,
          dtlOrder: item.dtlOrder,
          isDeleted: item.isDeleted,
          itemName: item.itemName,
          modifiedBy: item.modifiedBy,
          supplyAmount: item.supplyAmount,
          taxAmount: item.taxAmount,
          totalAmount: item.totalAmount,
          trDateTime: item.trDateTime,
        });

        const originalDetails = oriBill.billDetailList?.map(normalizeToBillDetailItem) || [];
        const newDetails = detailList?.map(normalizeToBillDetailItem) || [];

        const isEqual = _.isEqual(originalDetails, newDetails);
        return !isEqual;
      };

      const onConfirmBill = async (detailList?: any) => {
        const temp = _.cloneDeep(bill);
        let sendData: any = { billId: bill.billId };

        sendData.isConfirmed = !temp.isConfirmed;
        const pass = checkBillChanges(detailList);

        if (!pass) {
          // pass === false 또는 undefined 모두 처리
          const rnt = await updateBill({ scheduleId: bill.scheduleId.toString(), body: sendData });
          setBill({ ...temp, ...sendData });
          return rnt;
        } else {
          openToast({ content: "변경 사항이 있습니다. 먼저 저장 후 확정 해주세요 ", isSystemToast: true });
          return;
        }
      };

      /**
       * 저장 버튼 클릭
       */
      const saveBill = async (detailList: any) => {
        const temp = _.cloneDeep(bill);

        let sendData: any = { billId: oriBill.billId };

        const diffKeys = objDiff(oriBill, bill);
        if (diffKeys.length > 0) {
          for (let i = 0; i < diffKeys.length; i++) {
            const key = diffKeys[i];

            if (key !== "billDetailList") {
              sendData[key] = bill[key];
            }
          }
        }
        if (sendData.billStartTime !== "") {
          sendData.billStartTime = moment(temp.billStartTime).format(YmdFormat.WITH_TIME_ZONE);
        }
        if (sendData.billEndTime !== "") {
          sendData.billEndTime = moment(temp.billEndTime).format(YmdFormat.WITH_TIME_ZONE);
        }
        if (sendData.billIssueDate !== "") {
          sendData.billIssueDate = moment(temp.billIssueDate).format(YmdFormat.WITH_TIME_ZONE);
        }
        if (sendData.billDueDate !== "") {
          sendData.billDueDate = moment(temp.billDueDate).format(YmdFormat.WITH_TIME_ZONE);
        }

        // 세금계산서 수정 시 > 계산서 작성일 시간 오늘 날짜로 변경되는 이슈 수정 (harry 수정)
        if (sendData.pgcode !== "creditcard" && (sendData.taxInvoiceIssueDate ?? bill.taxInvoiceIssueDate) !== null) {
          const targetDate = sendData.taxInvoiceIssueDate ?? bill.taxInvoiceIssueDate;
          sendData.taxInvoiceIssueDate = moment(targetDate).format(YmdFormat.YYYY_MM_DD) + "T00:00:00+09:00";
        } else if (sendData.hasOwnProperty("taxInvoiceIssueDate")) {
          delete sendData.taxInvoiceIssueDate;
        }

        temp.billDetailList = detailList;
        setBill(temp);
        //저장 기능 처리
        sendData.billDetailList = detailList;
        delete sendData.billPayStatus;
        delete sendData.createdDate;
        delete sendData.modifiedDate;
        delete sendData.modifiedBy;
        delete sendData.createdBy;

        return await updateBill({ scheduleId: bill.scheduleId.toString(), body: sendData });
      };

      useEffect(() => {
        setBill(_.cloneDeep(row.original.bill));
      }, [row.original.bill]);

      return (
        <div>
          <div
            className="text-underline pointer"
            onClick={(e: any) => {
              e.preventDefault();
              setBill(_.cloneDeep(row.original.bill));
              setShowUpdateBill(true);
            }}
          >
            <div className="w-100 text-left cursor-pointer">{value ? <BaseTooltip contents={value} /> : "-"}</div>
          </div>
          {showUpdateBill && (
            <MakeBillPopup
              isChange={showUpdateBill}
              contractDetail={contractBaseInfo}
              showBtn={showBtn}
              bill={_.cloneDeep(bill)}
              setBill={setBill}
              onClose={onClosePopup}
              onConfirm={onConfirmBill}
              onClick={saveBill}
              messageModal={messageModal}
              setMessageModal={setMessageModal}
              getRounds={getRounds}
              allBill={row.original}
              buildingId={bill.buildingId}
              scheduleId={bill.scheduleId}
            />
          )}
          {messageModal.isOpen && (
            <BaseModal
              isOpen={messageModal.isOpen}
              btnRightTitle={"확인"}
              onClick={async () => {
                // harry 수정
                await getRounds();
                setMessageModal({ ...messageModal, isOpen: false });
              }}
              title={messageModal.message}
            ></BaseModal>
          )}
        </div>
      );
    },
    Footer: () => {
      return <p>합계 : </p>;
    },
  },
  {
    Header: "청구금액 (원)",
    accessor: "bill.totalAmount",
    sticky: "left",
    width: 200,
    Cell: ({ value }: ScheduleCellProps) => {
      return <p>{numberToStringWithComma(value) + " 원"}</p>;
    },
    Footer: ({ rows }) => {
      let sum = 0;
      rows.map((row) => {
        sum = sum + Number(row.original.bill.totalAmount);
      });
      return <p>{numberToStringWithComma(sum) + " 원"}</p>;
    },
  },
  {
    Header: "결제수단",
    accessor: "bill.pgcode",
    sticky: "left",
    width: 120,
    Cell: ({ value }: ScheduleCellProps) => {
      return (
        <div>
          {value && value === "virtualaccount" && <p>가상계좌</p>}
          {value && value === "creditcard" && <p>신용카드</p>}
          {value && value === "banktransfer" && <p>계좌이체</p>}
        </div>
      );
    },
  },
  {
    Header: "산출시작일",
    accessor: "bill.billStartTime",
    sticky: "left",
    width: 120,
    Cell: ({ row }: ScheduleCellProps) => {
      const start = moment(row.original.bill.billStartTime).format(ViewYmdFormat.YYYY_MM_DD_WEEK);
      return <p>{start}</p>;
    },
  },
  {
    Header: "산출종료일",
    accessor: "bill.billEndTime",
    sticky: "left",
    width: 120,
    Cell: ({ row }: ScheduleCellProps) => {
      const end = moment(row.original.bill.billEndTime).format(ViewYmdFormat.YYYY_MM_DD_WEEK);
      return <p>{end}</p>;
    },
  },
  {
    Header: "납부시작일시",
    accessor: "bill.billIssueDate",

    width: 120,
    Cell: ({ row }: ScheduleCellProps) => {
      return (
        <div>{row.original.bill.billIssueDate ? moment(row.original.bill.billIssueDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS) : "-"} </div>
      );
    },
  },
  {
    Header: "납부종료일시",
    accessor: "bill.billDueDate",
    width: 120,
    Cell: ({ row }: ScheduleCellProps) => {
      let payLimitDay = row.original.bill.billDueDate;
      return <div>{payLimitDay ? moment(payLimitDay, YmdFormat.WITH_TIME_ZONE).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS) : "-"} </div>;
    },
  },
  {
    Header: "수납확인",
    accessor: "recieve",
    width: 120,
    Cell: ({ row, rows, callList, contractBaseInfo, scheduleId, showBtn }: ScheduleCellProps) => {
      const [isChange, setIsChange] = useState(false);

      const pDatas = useMemo(() => {
        const datas = {
          row,
          rows,
          callList,
          contractBaseInfo,
          scheduleId,
          showBtn,
          isChange,
          setIsChange,
          type: "schedule",
        };
        return datas;
      }, [callList, contractBaseInfo, isChange, row, rows, scheduleId, showBtn]);

      const isNotYetDue = useMemo(() => {
        const limit = moment(row.original.bill.billDueDate, "YYYY-MM-DDTHH:mm:ss.sss+09:00").format(ViewYmdFormat.FULL);
        return moment(limit, ViewYmdFormat.FULL).isAfter(moment(ViewYmdFormat.FULL).format(ViewYmdFormat.FULL));
      }, [row.original.bill]);
      return (
        <div>
          <div>
            {showBtn && row.original.bill.billPayStatus !== "PAYMENT_SUCCESS" ? (
              <BaseButton
                title={row.original.receiptBundle.receiptList.length > 0 ? "수정" : "수납처리"}
                className="color-white size-small"
                disabled={
                  (Number(row.original.bill.totalAmount) > 0 || row.original.bill.billPayStatus === "PAYMENT_READY" ? false : true) || isNotYetDue
                }
                onClick={() => setIsChange(true)}
              />
            ) : (
              <BaseButton
                title={"수납확인"}
                className="color-white size-small"
                disabled={
                  /** billReceipt는 존재하지 않는 prop이지만 기존 UX가 달라지므로 보존 */
                  ((row.original as any).billReceipt !== null ? false : row.original.bill.billPayStatus === "PAYMENT_SUCCESS" ? false : true) ||
                  isNotYetDue
                }
                onClick={() => setIsChange(true)}
              />
            )}
            {isChange && <StorageProcessingModal processData={pDatas} />}
          </div>
        </div>
      );
    },
  },

  {
    Header: "즉시결제",
    accessor: "instantPayment",
    width: 120,
    Cell: ({ row, rows, showBtn, callList, contractBaseInfo }: ScheduleCellProps) => {
      const [confirmFlag, setConfirmFlag] = useState(false);
      const [prevStatus, setPrevStatus] = useState<string>("");

      const { openToast } = useToast();

      const { executeAsync: retryPaymentCall } = useApiOperation(retryPayment);

      const retryPay = useCallback(async () => {
        setConfirmFlag(false);
        const askReturn = await retryPaymentCall({ billId: Number(row.original.bill.billId) }).catch(() => undefined);

        if (askReturn && String(askReturn.data.data.content.result) === "true") {
          await callList();
          openToast({ content: "즉시결제가 정상처리 되었습니다.", isSystemToast: true });
        } else {
          openToast({ content: "즉시결제가 실패하였습니다. 관리자에게 문의해주세요.", isSystemToast: true });
        }
      }, [callList, openToast, row.original.bill.billId, retryPaymentCall]);
      const today = moment();
      const billDate = moment(row.original.bill.billIssueDate);

      const checkPrevStatus = useMemo(() => {
        let temp = _.cloneDeep(prevStatus);
        const finded = rows.find((r) => Number(r.id) === Number(row.id) - 1);

        if (finded) {
          temp = finded.original.bill.billPayStatus;
          setPrevStatus(finded.original.bill.billPayStatus);
        }
        return temp;
      }, [prevStatus, row.id, rows]);
      const getBillKey = useMemo(() => {
        let billkey: string | undefined = undefined;
        const typedContractBaseInfo = contractBaseInfo as DetailTotalData | undefined;
        if (typedContractBaseInfo && typedContractBaseInfo.scheduleList) {
          const find = typedContractBaseInfo.scheduleList.find((obj) => obj.supplyType === "RENTAL");

          if (find) {
            return find.billkey;
          }
        }

        return billkey;
      }, [contractBaseInfo]);

      return (
        <>
          {showBtn ? (
            <>
              <BaseButton
                title={"즉시결제"}
                className="color-white size-small"
                disabled={
                  row.original.bill.pgcode !== "creditcard" ||
                  getBillKey === null ||
                  getBillKey === undefined ||
                  getBillKey === "" ||
                  row.original.receiptBundle.receiptList.length > 0 ||
                  row.original.bill.billPayStatus === "PAYMENT_SUCCESS" ||
                  moment(today).isSameOrBefore(moment(billDate)) ||
                  (row.original.bill.billPayStatus === "PAYMENT_READY" && (checkPrevStatus === "" || checkPrevStatus === "PAYMENT_SUCCESS")
                    ? false
                    : true)
                }
                onClick={() => setConfirmFlag(true)}
              />
              <BaseModal
                title={"즉시 결제하시겠습니까?"}
                isOpen={confirmFlag}
                btnLeftTitle="취소"
                btnRightTitle="확인"
                onClick={() => retryPay()}
                onClose={() => setConfirmFlag(false)}
              ></BaseModal>
            </>
          ) : (
            "-"
          )}
        </>
      );
    },
  },
  {
    Header: "tid",
    accessor: "payment.tid",
    width: 160,
    Cell: ({ value, row }: ScheduleCellProps) => {
      let billShowTId = "-";
      const sortedList = _.sortBy(row.original.paymentList, ["transactionDate"]).reverse();

      if (sortedList.length > 0) {
        const payment = sortedList[0];

        billShowTId = payment.tid !== "" ? payment.tid : "-";

        if (payment.paymentCancel !== null) {
          billShowTId = payment.paymentCancel?.tid || "-";
        }
      }

      return <BaseTooltip contents={billShowTId} />;
    },
  },
  {
    Header: "연체기간(일)",
    accessor: "receiptBundle.totalOverdueDays",
    width: 120,
    Cell: ({ value }: ScheduleCellProps) => {
      return <div>{value ? value + "일" : "-"} </div>;
    },
  },
  {
    Header: "연체금액(원)",
    accessor: "receiptBundle.accruedOverdueAmount",
    width: 120,
    Cell: ({ value, row }: ScheduleCellProps) => {
      let realtimeOverdueAmount = 0;
      if (row.original.receiptBundle.realtimeOverdueAmount !== undefined) {
        realtimeOverdueAmount += Number(row.original.receiptBundle.realtimeOverdueAmount);
      }

      if (row.original.receiptBundle.accruedOverdueAmount !== undefined) {
        realtimeOverdueAmount += Number(row.original.receiptBundle.accruedOverdueAmount);
      }
      const today = moment();
      const billDate = moment(row.original.bill.billIssueDate);
      if (today.isSameOrAfter(billDate)) {
        return <div>{numberToStringWithComma(Number(realtimeOverdueAmount)) + "원"}</div>;
      } else {
        return <p>-</p>;
      }
    },
    Footer: ({ rows }) => {
      let sum = 0;
      rows.forEach((row) => {
        if (row.original.receiptBundle.realtimeOverdueAmount !== undefined) {
          sum += Number(row.original.receiptBundle.realtimeOverdueAmount);
        }
      });
      if (rows.length > 0) {
        return <p>{numberToStringWithComma(sum) + " 원"}</p>;
      } else {
        return <p>0 원</p>;
      }
    },
  },
  {
    Header: "거래일시",
    accessor: "receiptBundle.lastTrDateTime",
    width: 120,
    Cell: ({ value }: ScheduleCellProps) => {
      return <>{value ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS) : "-"}</>;
    },
  },
  {
    Header: "수납 전 금액",
    accessor: "receiptBundle.billTotalAmount",
    width: 160,
    Cell: ({ row, value }: ScheduleCellProps) => {
      const today = moment().format(ViewYmdFormat.FULL);
      const billDate = moment(row.original.bill.billIssueDate).format(ViewYmdFormat.FULL);
      if (moment(today).isSameOrAfter(moment(billDate))) {
        return <div>{numberToStringWithComma(Number(value)) + "원"}</div>;
      } else {
        return <p>-</p>;
      }
    },
    Footer: ({ rows }) => {
      const today = moment().format(ViewYmdFormat.FULL);

      let sum = 0;
      rows.map((row) => {
        const billDate = moment(row.original.bill.billIssueDate).format(ViewYmdFormat.FULL);
        if (moment(today).isSameOrAfter(moment(billDate))) {
          sum = sum + Number(row.original.receiptBundle.billTotalAmount);
        }
      });
      return <p>{numberToStringWithComma(sum) + " 원"}</p>;
    },
  },
  {
    Header: "수납금액",
    accessor: "receiptBundle.receiptTotalAmount",
    width: 160,
    Cell: ({ row, value }: ScheduleCellProps) => {
      const today = moment().format(ViewYmdFormat.FULL);
      const billDate = moment(row.original.bill.billIssueDate).format(ViewYmdFormat.FULL);
      if (moment(today).isSameOrAfter(moment(billDate))) {
        return <div>{numberToStringWithComma(Number(value)) + "원"}</div>;
      } else {
        return <p>-</p>;
      }
    },
    Footer: ({ rows }) => {
      const today = moment().format(ViewYmdFormat.FULL);

      let sum = 0;
      rows.map((row) => {
        const billDate = moment(row.original.bill.billIssueDate).format(ViewYmdFormat.FULL);
        if (moment(today).isSameOrAfter(moment(billDate))) {
          sum = sum + Number(row.original.receiptBundle.receiptTotalAmount);
        }
      });
      return <p>{numberToStringWithComma(sum) + " 원"}</p>;
    },
  },
  {
    Header: "잔액",
    accessor: "receiptBundle.billUnpaidAmount",
    width: 160,
    Cell: ({ row, value }: ScheduleCellProps) => {
      const today = moment().format(ViewYmdFormat.FULL);
      const billDate = moment(row.original.bill.billIssueDate).format(ViewYmdFormat.FULL);
      if (moment(today).isSameOrAfter(moment(billDate))) {
        return <div>{value > 0 ? "-" + numberToStringWithComma(Number(value)) + "원" : "0 원"}</div>;
      } else {
        return <p>-</p>;
      }
    },
    Footer: ({ rows }) => {
      const today = moment().format(ViewYmdFormat.FULL);

      let sum = 0;
      rows.map((row) => {
        const billDate = moment(row.original.bill.billIssueDate).format(ViewYmdFormat.FULL);
        if (moment(today).isSameOrAfter(moment(billDate))) {
          sum = sum + Number(row.original.receiptBundle.billUnpaidAmount);
        }
      });
      return <p>{sum > 0 ? "-" + numberToStringWithComma(sum) + " 원" : "0 원"}</p>;
    },
  },
  {
    Header: "승인",
    accessor: "bill.isConfirmed",
    width: 80,
    Cell: ({ value, row }: ScheduleCellProps) => {
      return (
        <div className={`${String(value) === "true" ? "status F" : "status G"}`}>
          <p>{String(value) === "true" ? "확정" : "미확정"}</p>
        </div>
      );
    },
  },
  {
    Header: "청구서 상태",
    accessor: "bill.billPayStatus",
    width: 120,
    Cell: ({ value, row }: ScheduleCellProps) => {
      // 연체 표시 : -
      let billIssuDay = moment(row.original.bill.billDueDate).format(ViewYmdFormat.YYYY_MM_DD);
      let today = moment().format(ViewYmdFormat.YYYY_MM_DD);

      if (row.original.bill.billPayStatus === "PAYMENT_READY" && moment(today).isAfter(moment(billIssuDay))) {
        value = "PAYMENT_DELAY";
      }

      return <PaymentStatusView value={value} />;
    },
  },

  {
    Header: "청구서 이메일",
    accessor: "bill.billPayStatus.2",
    width: 120,
    Cell: ({ row, sendingBillEmail }: ScheduleCellProps) => {
      const sendHistory = row.original.billEtc.chargeEmailSentAt;

      return (
        <div>
          <BaseButton
            title={sendHistory === "" ? "발송" : "재발송"}
            className="color-white size-small"
            disabled={row.original.bill.isConfirmed === false}
            onClick={() => {
              sendingBillEmail({
                billId: row.original.bill.billId,
                billIssueDate: row.original.bill.billIssueDate,
                sendHistory,
              });
            }}
          />
        </div>
      );
    },
  },

  {
    Header: "최근 청구서 발송일시",
    accessor: "billEtc.chargeEmailSentAt",
    width: 160,
    Cell: ({ value, row }: ScheduleCellProps) => {
      return <p>{value !== "" ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS) : "-"}</p>;
    },
  },
  {
    Header: "계산서 작성일",
    accessor: "bill.taxInvoiceIssueDate",
    width: 120,
    Cell: ({ value, row }: ScheduleCellProps) => {
      return <p>{value !== "" ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_WEEK) : "-"}</p>;
    },
  },
  {
    Header: "발행 유형",
    accessor: "bill.taxPurposeType",
    width: 160,
    Cell: ({ value, row, handleEditTaxPurposeType }: ScheduleCellProps) => {
      const rowData = row.original;

      return (
        <>
          <BaseButton
            title="수정"
            className="color-white size-small mr5"
            disabled={rowData.taxInvoiceList.length !== 0} // 세금계산서 발행 건이 있으면 수정 불가
            onClick={() => {
              handleEditTaxPurposeType?.({
                billId: row.original.bill.billId,
                scheduleId: row.original.bill.scheduleId.toString(),
                taxPurposeType: value,
              });
            }}
          />
          <span>{value === "BILLING" ? "청구" : "영수"}</span>
        </>
      );
    },
  },

  {
    Header: () => (
      <div className="flex-center">
        <p className="mr3">세금 계산서 발행</p>
        <InvoiceIssueColumnTooltip />
      </div>
    ),
    accessor: "bill.billPayStatus.10",
    width: 160,
    Cell: getInvoiceIssueCellRednerer("TAX_PLUS"),
  },
  {
    Header: "세금 계산서 이메일",
    accessor: "",
    width: 120,
    Cell: getInvoiceMailCellRenderer("TAX_PLUS"),
  },

  {
    Header: "세금계산서 상태",
    accessor: "",
    width: 160,
    Cell: getInvoiceStatusCellRenderer("TAX_PLUS"),
  },

  {
    Header: "세금계산서 승인번호",
    accessor: "TAX_PLUS",
    width: 240,
    Cell: getInvoiceNumberCellRenderer("TAX_PLUS"),
  },
  {
    Header: "세금계산서 발행일",
    accessor: "taxInvoiceIssueDate",
    width: 120,
    Cell: getInvoiceIssueDateCellRenderer("TAX_PLUS"),
  },

  {
    Header: () => (
      <div className="flex-center">
        <p className="mr3">계산서 발행</p>
        <InvoiceIssueColumnTooltip />
      </div>
    ),
    accessor: "bill.billPayStatus.11",
    width: 120,
    Cell: getInvoiceIssueCellRednerer("TAX_NONE"),
  },

  {
    Header: "계산서 이메일",
    accessor: "",
    width: 120,
    Cell: getInvoiceMailCellRenderer("TAX_NONE"),
  },
  {
    Header: "계산서 상태",
    accessor: "",
    width: 160,
    Cell: getInvoiceStatusCellRenderer("TAX_NONE"),
  },
  {
    Header: "계산서 승인 번호",
    accessor: "TAX_NONE",
    width: 240,
    Cell: getInvoiceNumberCellRenderer("TAX_NONE"),
  },
  {
    Header: "계산서 발행일",
    accessor: "cashBillIssueDate",
    width: 120,
    Cell: getInvoiceIssueDateCellRenderer("TAX_NONE"),
  },
];
export default ScheduleInfoColumns;
